import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CharacterCard } from "@web/components/CharacterCard";
import { LoadingSpinner } from "@web/components/LoadingSpinner";
import { Navbar } from "@web/components/Navbar";
import { Footer } from "@web/route-services/index/Footer";
import { VerticalCard } from "@web/route-services/index/VerticalCard";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";

export const Route = createFileRoute("/character/$characterId")({
  component: CharacterPage
});

function CharacterPage() {
  const { characterId } = Route.useParams();
  const { access, settings } = useAppContext();

  const id = Number(characterId);
  const {
    data: character,
    isError,
    isPending
  } = useQuery({
    queryKey: [id],
    queryFn: async () => {
      return await access.character.getByID(id);
    }
  });

  const nsfwOK = settings?.nsfwOK || false;
  const { data: similarCharactersData, isPending: isSimilarPending } = useQuery({
    queryKey: ["similarCharacters", id, nsfwOK],
    queryFn: async () => {
      return await access.character.getSimilar(id, nsfwOK);
    }
  });

  if (isPending || !character) {
    return (
      <div className="bg-background flex h-screen w-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-background flex h-screen w-screen">
      <img
        className="absolute inset-0 z-0 h-full w-full object-cover"
        src={character.banner ?? character.avatar}
        alt=""
      />
      <div className="z-1 absolute inset-0 bg-black/70 backdrop-blur-xl" />
      <Navbar />
      <div
        style={{ scrollbarGutter: "stable" }}
        className="scroll-home-page z-10 ml-2.5 flex w-full flex-col overflow-auto pb-8"
      >
        <div className="flex w-full justify-center p-4">
          {isError ? (
            <p className="text-tx-primary text-lg">Failed to load character details</p>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.2,
                  ease: [0, 0.71, 0.2, 1.01]
                }}
                className=""
              >
                <CharacterCard char={character} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        {/* Similar Characters Section */}
        {similarCharactersData && similarCharactersData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mx-auto w-full max-w-7xl px-4 pb-12"
          >
            <button
              onClick={() => {
                document.getElementById("similar-characters")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start"
                });
              }}
              className="text-tx-primary hover:text-tx-primary/80 mb-6 text-2xl font-semibold transition-colors"
              id="similar-characters"
            >
              Similar Characters
            </button>
            <div className="mb-14 grid grid-cols-2 justify-center gap-1.5 scroll-smooth px-1.5 transition duration-500 ease-out sm:grid-cols-3 md:flex md:flex-wrap md:gap-4 md:px-0">
              {isSimilarPending
                ? Array(3)
                    .fill(0)
                    .map((_, i) => <VerticalCard key={i} isPending />)
                : similarCharactersData.map((char) => <VerticalCard key={char.id} char={char} isPending={isPending} />)}
            </div>
          </motion.div>
        )}
        <Footer />
      </div>
    </div>
  );
}
